import { useState, useEffect, createContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout, Drawer, Affix } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Sidenav from './Sidenav';
import Header from './Header';
import Footer from './Footer';

import moment from 'moment';

const { Header: AntHeader, Content, Sider } = Layout;

export const DateContext = createContext(null);
export const AccountContext = createContext(null);

function Main({ children }) {
  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState('right');
  const [sidenavColor, setSidenavColor] = useState('#1890ff');
  const [sidenavType, setSidenavType] = useState('transparent');
  const [fixed, setFixed] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState([
    moment.utc().add(-7, 'day').minute(0).second(0),
    moment.utc().minute(0).second(0),
  ]);
  const [accounts, setAccounts] = useState(null);
  const [filteredAccount, setFilteredAccount] = useState(null);

  const openDrawer = () => setVisible(!visible);
  const handleSidenavType = (type) => setSidenavType(type);
  const handleSidenavColor = (color) => setSidenavColor(color);
  const handleFixedNavbar = (type) => setFixed(type);
  const handleAutoRefresh = (type) => setAutoRefresh(type);

  let { pathname } = useLocation();
  pathname = pathname.replace('/', '');

  useEffect(() => {
    if (pathname === 'rtl') {
      setPlacement('left');
    } else {
      setPlacement('right');
    }
  }, [pathname]);

  useEffect(() => {
    const refreshTimeRange = () => {
      setTimeRange([timeRange[0], moment.utc().minute(0).second(0)]);
    };

    let timeoutId;

    const scheduleNextRefresh = () => {
      const now = moment.utc();
      const next = now.clone().minute(15).second(0).millisecond(0);
      if (now.minute() >= 15) {
        next.add(1, 'hour');
      }
      const ms = next.diff(now);

      timeoutId = setTimeout(() => {
        refreshTimeRange();
        scheduleNextRefresh();
      }, ms);
    };

    if (autoRefresh) {
      scheduleNextRefresh();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  return (
    <QueryClientProvider
      client={
        new QueryClient({
          defaultOptions: { queries: { staleTime: 1000 * 60 * 10 } },
        })
      }
    >
      <ReactQueryDevtools initialIsOpen={false} />
      <AccountContext.Provider
        value={{ accounts, setAccounts, filteredAccount, setFilteredAccount }}
      >
        <Layout
          className={`layout-dashboard ${
            pathname === 'profile' ? 'layout-profile' : ''
          } ${pathname === 'rtl' ? 'layout-dashboard-rtl' : ''}`}
        >
          <Drawer
            title={false}
            placement={placement === 'right' ? 'left' : 'right'}
            closable={false}
            onClose={() => setVisible(false)}
            visible={visible}
            key={placement === 'right' ? 'left' : 'right'}
            width={250}
            className={`drawer-sidebar ${
              pathname === 'rtl' ? 'drawer-sidebar-rtl' : ''
            } `}
          >
            <Layout
              className={`layout-dashboard ${
                pathname === 'rtl' ? 'layout-dashboard-rtl' : ''
              }`}
            >
              <Sider
                trigger={null}
                width={250}
                theme='light'
                className={`sider-primary ant-layout-sider-primary ${
                  sidenavType === '#fff' ? 'active-route' : ''
                }`}
                style={{ background: sidenavType }}
              >
                <Sidenav color={sidenavColor} />
              </Sider>
            </Layout>
          </Drawer>
          <Sider
            breakpoint='lg'
            collapsedWidth='0'
            onCollapse={(collapsed, type) => {
              console.log(collapsed, type);
            }}
            trigger={null}
            width={250}
            theme='light'
            className={`sider-primary ant-layout-sider-primary ${
              sidenavType === '#fff' ? 'active-route' : ''
            }`}
            style={{ background: sidenavType }}
          >
            <Sidenav color={sidenavColor} />
          </Sider>
          <Layout>
            <DateContext.Provider value={{ timeRange, setTimeRange }}>
              {fixed ? (
                <Affix>
                  <AntHeader className={`${fixed ? 'ant-header-fixed' : ''}`}>
                    <Header
                      onPress={openDrawer}
                      name={pathname}
                      subName={pathname}
                      fixed={fixed}
                      handleSidenavColor={handleSidenavColor}
                      handleSidenavType={handleSidenavType}
                      handleFixedNavbar={handleFixedNavbar}
                      handleAutoRefresh={handleAutoRefresh}
                      autoRefresh={autoRefresh}
                    />
                  </AntHeader>
                </Affix>
              ) : (
                <AntHeader className={`${fixed ? 'ant-header-fixed' : ''}`}>
                  <Header
                    onPress={openDrawer}
                    name={pathname}
                    subName={pathname}
                    fixed={fixed}
                    handleSidenavColor={handleSidenavColor}
                    handleSidenavType={handleSidenavType}
                    handleFixedNavbar={handleFixedNavbar}
                    handleAutoRefresh={handleAutoRefresh}
                    autoRefresh={autoRefresh}
                  />
                </AntHeader>
              )}
              <Content className='content-ant'>{children}</Content>
              <Footer />
            </DateContext.Provider>
          </Layout>
        </Layout>
      </AccountContext.Provider>
    </QueryClientProvider>
  );
}

export default Main;
